import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Temas disponíveis.
 */
export enum Themes {
    DARK = 'dark',
    DARK_NEXO = 'dark-nexo',
    LIGHT = 'light',
    LIGHT_NEXO = 'light-nexo',
}

/** Chave usada para persistir o tema escolhido pelo usuário. */
const THEME_STORAGE_KEY = 'conversai.theme';

/** Tema padrão da aplicação quando não há escolha persistida. */
const DEFAULT_THEME = Themes.DARK_NEXO;

/**
 * Gerenciador de temas.
 * Deve ser utilizado para realizar a troca de temas da aplicação.
 *
 */
@Injectable({
    providedIn: 'root',
})
export class ThemeManager {
    private currentTheme: BehaviorSubject<Themes> = new BehaviorSubject<Themes>(
        DEFAULT_THEME,
    );
    private currentTheme$ = this.currentTheme.asObservable();

    constructor(@Inject(DOCUMENT) private document: Document) {}

    /**
     * Stream que emite o tema atual a cada troca.
     *
     * @returns Observable do tema corrente.
     */
    themeChangedObservable(): Observable<Themes> {
        return this.currentTheme$;
    }

    /**
     * Tema atualmente aplicado.
     */
    getCurrentTheme(): Themes {
        return this.currentTheme.value;
    }

    /**
     * Troca o tema da aplicação alternando a classe no `<body>` e persiste a
     * escolha em `localStorage`.
     *
     * Os temas são definidos como classes (`theme-dark-nexo`/`theme-light-nexo`)
     * que mapeiam os tokens semânticos em `tokens.css`. Adicionar um tema novo é
     * apenas criar um bloco `.theme-x` lá.
     */
    change(theme: Themes): void {
        this.currentTheme.next(theme);
        const body = this.document.body;
        const themeClasses = Array.from(body.classList).filter((cssClass) =>
            cssClass.startsWith('theme-'),
        );
        body.classList.remove(...themeClasses);
        body.classList.add(`theme-${theme}`);
        this.persist(theme);
    }

    /**
     * Aplica o tema persistido (ou o padrão `dark-nexo` se não houver/for
     * inválido). Chamado no boot (APP_INITIALIZER), dentro da janela do
     * loader inicial — sem flash visível.
     */
    applyStoredTheme(): void {
        this.change(this.readStoredTheme() ?? DEFAULT_THEME);
    }

    /** Lê o tema salvo, validando contra os valores conhecidos. */
    private readStoredTheme(): Themes | null {
        try {
            const stored = localStorage.getItem(THEME_STORAGE_KEY);
            const valid = Object.values(Themes) as string[];
            return stored && valid.includes(stored) ? (stored as Themes) : null;
        } catch {
            // localStorage indisponível (modo privado/SSR) — usa o padrão.
            return null;
        }
    }

    /** Persiste o tema escolhido (ignora falhas de storage). */
    private persist(theme: Themes): void {
        try {
            localStorage.setItem(THEME_STORAGE_KEY, theme);
        } catch {
            // Sem persistência se o storage estiver indisponível.
        }
    }
}
